import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Heading from "../../../../../../common/element/Heading";
import BtnPrimary from "../../../../../../common/element/Buttons/btnPrimary";
import BtnLight from "../../../../../../common/element/Buttons/btnLight";
import Dialog from "../../../../../../common/element/Dialog/Dialog";
import Table from "../../../../../../common/element/Table";
import Avatar from "../../../../../../common/element/Avatar";
import useUnauthorizedHandler from "../../../../../../common/snippet/useUnauthorizedHandler";
import { Pagination } from "../../../../../../common/pagination";
import ReactPaginate from "react-paginate";
import { setBreadcrumbs } from "../../../../../../redux/classes/breadcrumbsSlice";
import {
  GetSpecificOrgNotes,
  CreateOrgNote,
  UpdateOrgNote,
  DeleteOrgNote
} from "../../../../../../actions/client-portal/AllEmailApi";
import { GetProviderOverviewForClient } from "../../../../../../actions/client-portal/AllSiteApi";
import { decryptId } from "../../../../../../common/encryptDecrypt/encryptdecrypt";
import moment from "moment/moment";
import { debounce } from "../../../../../../common/snippet/Debouncing";
import { GetSignedUrlByKey } from "../../../../../../actions/public/GetSignedUrlByKey";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader";
import Upload from "../../../../../../common/element/Upload";
import Validations from "../../../../../../common/404/Validations";
import ThreeDotLoader from "../../../../../../common/element/ButtonLoader/ThreeDotLoader";

const { Tr, Th, Td, THead, TBody } = Table;

const ProviderNotesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organisationId, specificStatus } = useParams();
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const getLoginUserdta = JSON.parse(localStorage.getItem("LoginUserData"));
  const currentUserId = getLoginUserdta?.userName?.user_id;

  const [providerData, setProviderData] = useState(null);
  const [contactTypeId, setContactTypeId] = useState("");
  const AllContactType = useSelector((state) => state?.AllContactTypes);

  const [loadData, setLoadData] = useState(false);
  const [dialogIsOpen, setIsOpen] = useState(false);
  const [dialogNoteOpen, setNoteOpen] = useState(false);
  const [notesData, setNotesData] = useState("");
  const [countData, setCountData] = useState(0);
  const [noteSpecific, setNoteSpecific] = useState("");
  const [attachedFiles, setAttachedFiles] = useState("");
  
  const [dialogEditOpen, setEditOpen] = useState(false);
  const [editSpecificNote, setEditSpecificNote] = useState("");
  
  const [dialogDeleteOpen, setDeleteOpen] = useState(false);
  const [deleteSpecificNote, setDeleteSpecificNote] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const [totalPages, setTotalPages] = useState(1);

  // Extract function assignment ID of the provider
  const providerFaId = Array.isArray(providerData?.org_fun_assign)
    ? providerData?.org_fun_assign?.[0]?.function_assignment_id
    : providerData?.org_fun_assign?.function_assignment_id;

  const [filterData, setFilterData] = useState({
    page: 1,
    limit: 10,
    sort: "created_date",
    order: "desc",
    search: "",
    function_assignment_id: ""
  });

  // Fetch contact type ID
  useEffect(() => {
    if (AllContactType) {
      const contactTypeid = AllContactType.find(
        (item) => item.contact_type === "provider primary"
      )?.contact_type_id;
      setContactTypeId(contactTypeid);
    }
  }, [AllContactType]);

  // Fetch provider overview details on mount
  const fetchProviderDetails = useCallback(async () => {
    if (organisationId && (contactTypeId || AllContactType)) {
      const targetContactTypeId = contactTypeId || AllContactType?.find(
        (item) => item.contact_type === "provider primary"
      )?.contact_type_id;

      try {
        const response = await dispatch(
          GetProviderOverviewForClient({
            organisation_id: decryptId(organisationId),
            contact_type_id: targetContactTypeId
          })
        );
        if (response.success) {
          setProviderData(response.data);
        }
      } catch (error) {
        console.error("Error fetching provider overview:", error);
      }
    }
  }, [dispatch, organisationId, contactTypeId, AllContactType]);

  useEffect(() => {
    fetchProviderDetails();
  }, [fetchProviderDetails]);

  // Set breadcrumbs and function assignment ID once provider data is available
  useEffect(() => {
    if (providerData) {
      const faId = Array.isArray(providerData?.org_fun_assign)
        ? providerData?.org_fun_assign?.[0]?.function_assignment_id
        : providerData?.org_fun_assign?.function_assignment_id;

      setFilterData((prev) => ({
        ...prev,
        function_assignment_id: faId
      }));

      dispatch(
        setBreadcrumbs([
          { label: "View All Providers", url: "/client/provider", home: "/client/dashboard" },
          {
            label: `${providerData?.trading_name}`,
            url: `/client/provider/specific-provider/${encodeURIComponent(organisationId)}/${encodeURIComponent(specificStatus)}`,
          },
          {
            label: "Notes",
            url: `/client/provider/specific-provider/${encodeURIComponent(organisationId)}/${encodeURIComponent(specificStatus)}/notes`,
          },
        ])
      );
    }
  }, [providerData, organisationId, specificStatus, dispatch]);

  const updateDataFromApi = (arrayData) => {
    let notes_data = [];
    for (let key in arrayData) {
      let createdDate = moment(arrayData[key].created_date).format(
        "HH:mm DD/MM/YYYY"
      );
      notes_data.push({
        note_id: arrayData[key].note_id,
        note_heading: arrayData[key].note_heading,
        note: arrayData[key].note,
        created_date: createdDate,
        first_name: arrayData[key].first_name,
        last_name: arrayData[key].last_name,
        avatar: arrayData[key].avatar,
        attachment: arrayData[key].note_attachs,
        created_by: arrayData[key].created_by,
      });
    }
    setNotesData(notes_data);
  };

  const GetSpecificOrgNotesCallback = useCallback(() => {
    if (filterData.function_assignment_id) {
      setLoadData(false);
      dispatch(GetSpecificOrgNotes(filterData)).then((response) => {
        if (response.success) {
          setLoadData(true);
          const arrayData = response.data.rows;
          setCountData(response.data.count);
          updateDataFromApi(arrayData);
          setTotalPages(Math.ceil(response.data.count / filterData.limit));
        } else if (response.status === 401) {
          setLoadData(true);
          handleUnauthorizedAccess();
        }
      });
    }
  }, [dispatch, filterData]);

  useEffect(() => {
    GetSpecificOrgNotesCallback();
  }, [filterData.function_assignment_id, filterData.page, filterData.limit, filterData.sort, filterData.order, filterData.search, GetSpecificOrgNotesCallback]);

  const handleFile = (fileUrl) => {
    if (fileUrl) {
      dispatch(
        GetSignedUrlByKey({ url: fileUrl }, "client")
      ).then((response) => {
        if (response.success) {
          const signedUrl = response?.data;
          if (signedUrl) {
            window.location.href = signedUrl;
          }
        }
      });
    }
  };

  const openDialog = () => {
    setIsOpen(true);
  };
  const onDialogClose = () => {
    setIsOpen(false);
  };

  const openNoteDialog = (item) => {
    let files = [];
    item.attachment && item.attachment.length > 0 &&
      item.attachment.forEach((data) => {
        const file = data.note_attachment;
        const file_id = data.note_attachment_id;
        const parts = data.note_attachment.split("-");
        let FileName;
        if (parts.length >= 3) {
          FileName = parts[parts.length - 1];
          FileName = FileName.replace(/%/g, ' ');
        }
        files.push({
          name: FileName,
          file: file,
          id: file_id
        });
        setAttachedFiles(files);
      });
    setNoteSpecific(item);
    setNoteOpen(true);
  };

  const onNoteDialogClose = () => {
    setNoteOpen(false);
    setNoteSpecific("");
  };

  const openEditDialog = (item) => {
    setEditSpecificNote(item);
    setEditOpen(true);
  };

  const onEditDialogClose = () => {
    setEditOpen(false);
    setEditSpecificNote("");
  };

  const openDeleteDialog = (item) => {
    setDeleteSpecificNote(item);
    setDeleteOpen(true);
  };

  const onDeleteDialogClose = () => {
    setDeleteOpen(false);
    setDeleteSpecificNote("");
  };

  const handleDeleteNote = () => {
    if (deleteSpecificNote) {
      setButtonDisabled(true);
      dispatch(DeleteOrgNote(deleteSpecificNote.note_id)).then((response) => {
        if (response?.success === true) {
          setDeleteOpen(false);
          setDeleteSpecificNote("");
          GetSpecificOrgNotesCallback();
        } else if (response?.status === 401) {
          handleUnauthorizedAccess();
        }
      }).catch((error) => {
        console.error(error);
      }).finally(() => {
        setButtonDisabled(false);
      });
    }
  };

  const handleSeachChange = (e) => {
    setFilterData((prev) => ({
      ...prev,
      search: e.target.value.trim(),
      page: 1
    }));
  };
  const inputchangeSearch = useCallback(debounce(handleSeachChange), []);

  const handlePageChange = ({ selected }) => {
    setFilterData((prev) => ({
      ...prev,
      page: selected + 1
    }));
  };

  const handleSort = (column) => {
    setFilterData((prev) => ({
      ...prev,
      sort: column,
      order: prev.order === "asc" ? "desc" : "asc"
    }));
  };

  const updatePageLimit = (newLimit) => {
    setFilterData((prev) => ({
      ...prev,
      limit: newLimit?.limit ? newLimit?.limit : newLimit ? newLimit : 10,
      page: 1
    }));
  };

  const navigateBack = () => {
    navigate(`/client/provider/specific-provider/${encodeURIComponent(organisationId)}/${encodeURIComponent(specificStatus)}`);
  };

  return (
    <React.Fragment>
      {/* Top Header Card */}
      <div className="flex flex-row justify-between items-center mb-5 border-b pb-5">
        <div className="flex items-center">
          <div className="h-16 w-16 rounded-full overflow-hidden mr-5 avtar_img_round">
            <img src="/assets/img/avatars/thumb-8.jpg" alt="" />
          </div>
          <div className="flex flex-col">
            <div className="flex gap-6 items-center">
              <span className="text-gray-400 mr-1 font-semibold">ID:</span>  
              <span className="text-gray-900 font-medium">
                {providerData?.organisation_id ? providerData?.organisation_id : '-'}
              </span> 
              <span className="text-gray-900">|</span>
              <span className="text-gray-900 font-medium">
                <span className="text-gray-400 mr-1 font-semibold">ABN:</span>
                {providerData?.abn_nzbn ? providerData?.abn_nzbn : "00-000-000-000"}
              </span>
            </div>
            <Heading className="mb-2">
              {providerData?.trading_name ? providerData?.trading_name : "-"}
            </Heading>
            <div className="flex gap-6 items-center">
              <span className={`badge ${decryptId(specificStatus) === "Compliant" ? "bg-success-50 text-success-700" : "bg-error-50 text-error-700"} py-0.5 px-2 rounded-full font-medium`}>
                {decryptId(specificStatus)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <BtnLight
            className="w-32 text-xs text-gray-700 font-semibold flex items-center justify-center"
            onClick={navigateBack}
          >
            Back to Provider
          </BtnLight>
        </div>
      </div>

      <div className="flex justify-between items-center mb-5">
        <Heading>Notes</Heading>
        {providerFaId && (
          <BtnPrimary
            onClick={openDialog}
            className="w-29 text-xs flex items-center justify-center"
            variant="solid"
          >
            Add note{" "}
            <span className="ml-3">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 4.16699V15.8337M4.16666 10.0003H15.8333" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </BtnPrimary>
        )}
      </div>

      <div className="flex justify-between mb-5 mt-5">
        <div className="w-80 h-10">
          <div className="input-wrapper">
            <div className="input-suffix-start left-3.5">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.5 17.5L13.875 13.875M16.25 9.375C16.25 13.1719 13.1719 16.25 9.375 16.25C5.57804 16.25 2.5 13.1719 2.5 9.375C2.5 5.57804 5.57804 2.5 9.375 2.5C13.1719 2.5 16.25 5.57804 16.25 9.375Z" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <input
              type="text"
              name="hs-table-search"
              className="input pl-11 w-full input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Search"
              onChange={inputchangeSearch}
            />
          </div>
        </div>
      </div>

      <div className="pb-6">
        <div className="w-full inline-block align-middle">
          <div className="overflow-hidden border rounded-lg border-gray-200">
            <Table className="min-w-full divide-y divide-gray-200 notes-table">
              <THead className="bg-gray-100">
                <Tr>
                  <Th scope="col" className="bg-gray-100 px-4 py-3 font-medium text-left text-gray-600">
                    <span className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort("created_date")}>
                      Created date
                      <svg
                        className={`transform ${filterData.sort === "created_date" && filterData.order === "asc" ? "rotate-180" : ""}`}
                        width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M8.712 3.333v9.334m0 0L13.38 8m-4.667 4.667L4.045 8" stroke="#475467" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </Th>
                  <Th className="bg-gray-100 px-4 py-3 font-medium text-left text-gray-600">Created by</Th>
                  <Th className="bg-gray-100 px-4 py-3 font-medium text-left text-gray-600 w-2/5">Note</Th>
                  <Th className="bg-gray-100 px-4 py-3 font-medium text-center text-gray-600">Action</Th>
                </Tr>
              </THead>
              <TBody className="divide-y divide-gray-200">
                {loadData && notesData && notesData.length > 0 ? (
                  notesData.map((item) => (
                    <Tr key={item?.note_id}>
                      <Td className="px-4 py-3.5 font-medium text-gray-900">{item?.created_date}</Td>
                      <Td className="px-4 py-3.5 font-medium text-gray-900">
                        <span className="text-gray-800 font-medium">
                          {item?.first_name + " " + item?.last_name}
                        </span>
                      </Td>
                      <Td className="px-4 py-3.5 font-medium text-gray-900">
                        <span className="text-gray-900">
                          {item?.note_heading ? item?.note_heading : "-"}
                        </span>
                        <p className="text-gray-600 mt-1">{item?.note ? item?.note : "-"}</p>
                      </Td>
                      <Td className="px-4 py-3.5 font-medium text-gray-900">
                        <div className="flex gap-4 justify-center items-center">
                          {/* View Action */}
                          <span
                            onClick={() => openNoteDialog(item)}
                            className="cursor-pointer text-gray-500 hover:text-blue-700"
                            title="View"
                          >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.66666 10C1.66666 10 4.99999 3.33331 10 3.33331C15 3.33331 18.3333 10 18.3333 10C18.3333 10 15 16.6666 10 16.6666C4.99999 16.6666 1.66666 10 1.66666 10Z" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61927 11.3807 7.5 10 7.5C8.61927 7.5 7.5 8.61927 7.5 10C7.5 11.3807 8.61927 12.5 10 12.5Z" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                          {/* Edit Action */}
                          {item.created_by === currentUserId && (
                            <>
                              <span
                                onClick={() => openEditDialog(item)}
                                className="cursor-pointer text-gray-500 hover:text-blue-700"
                                title="Edit"
                              >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3.063 15.096c.039-.344.058-.517.11-.678.046-.143.111-.278.194-.404.093-.141.216-.264.461-.509L14.833 2.5a2.357 2.357 0 1 1 3.334 3.333L7.16 16.84c-.245.245-.367.367-.509.46a1.667 1.667 0 0 1-.404.195c-.16.052-.333.07-.678.11l-2.82.313.313-2.82z" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </span>
                              {/* Delete Action */}
                              <span
                                onClick={() => openDeleteDialog(item)}
                                className="cursor-pointer text-gray-500 hover:text-red-700"
                                title="Delete"
                              >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M2.5 5H4.16667H17.5M5.83333 5V3.33333C5.83333 2.8913 6.00893 2.46738 6.32149 2.15482C6.63405 1.84226 7.05797 1.66667 7.5 1.66667H12.5C12.942 1.66667 13.366 1.84226 13.6785 2.15482C13.9911 2.46738 14.1667 2.8913 14.1667 3.33333V5M15.8333 5V16.6667C15.8333 17.1087 15.6577 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83333C5.3913 18.3333 4.96738 18.1577 4.65482 17.8452C4.34226 17.5326 4.16667 17.1087 4.16667 16.6667V5H15.8333Z" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </span>
                            </>
                          )}
                        </div>
                      </Td>
                    </Tr>
                  ))
                ) : loadData ? (
                  <Tr>
                    <Td colSpan="4" className="text-center py-6 text-gray-500">No notes found.</Td>
                  </Tr>
                ) : (
                  <Tr>
                    <Td colSpan="4" className="text-center py-6">
                      <ThreeDotBouncing />
                    </Td>
                  </Tr>
                )}
              </TBody>
            </Table>
          </div>
        </div>
      </div>

      {loadData && (
        <div className="flex justify-between mt-3">
          <Pagination
            totalCount={countData}
            pageNumber={filterData.page}
            displayPage={notesData?.length}
            UpdatePageLimit={updatePageLimit}
            allfilters={filterData}
          />
          {countData > 0 && (
            <ReactPaginate
              className="flex justify-content-end p-2 align-items-center pagination-wrap"
              previousLabel={"Prev"}
              nextLabel={"Next"}
              breakLabel={"..."}
              breakClassName={"break-me"}
              pageCount={totalPages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={handlePageChange}
              containerClassName={"pagination"}
              activeClassName={"active"}
              previousLinkClassName={"prev-link"}
              nextLinkClassName={"page-link"}
              breakLinkClassName={"prev-link"}
              previousClassname={"px-4 rounded-md bg-blue-700 text-white"}
              nextClassname={"px-4 rounded-md bg-blue-700 text-white"}
              pageLinkClassName={"px-4 rounded-md"}
              activeLinkClassName={"w-9 h-9 bg-blue-700 text-white"}
              disabledClassName={"px-5 opacity-50 cursor-not-allowed"}
              renderOnZeroPageCount={null}
              forcePage={filterData.page - 1}
            />
          )}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog
        isOpen={dialogIsOpen}
        onClose={onDialogClose}
        onRequestClose={onDialogClose}
        bodyOpenClassName="overflow-hidden"
        className="addContact-dialog"
      >
        <div className="flex border-b mb-5 pb-5 px-6 pt-6">
          <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4V20M20 12L4 12" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <div>
            <h5 className="mb-1 text-gray-900 text-base font-semibold">Add a note</h5>
            <p className="text-gray-600">Create a new note for {providerData?.trading_name}</p>
          </div>
        </div>
        {providerFaId && (
          <AddNoteForm
            providerFaId={providerFaId}
            providerData={providerData}
            onDialogClose={onDialogClose}
            fetchData={GetSpecificOrgNotesCallback}
          />
        )}
      </Dialog>

      {/* View Dialog */}
      <Dialog
        isOpen={dialogNoteOpen}
        onClose={onNoteDialogClose}
        onRequestClose={onNoteDialogClose}
        bodyOpenClassName="overflow-hidden"
        className="addContact-dialog"
      >
        <div className="flex border-b mb-5 pb-5 px-6 pt-6">
          <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <path d="M11 4.67992H6.8C5.11984 4.67992 4.27976 4.67992 3.63803 5.0069C3.07354 5.29452 2.6146 5.75346 2.32698 6.31794C2 6.95968 2 7.79976 2 9.47992V17.8799C2 19.5601 2 20.4002 2.32698 21.0419C2.6146 21.6064 3.07354 22.0653 3.63803 22.3529C4.27976 22.6799 5.11984 22.6799 6.8 22.6799H15.2C16.8802 22.6799 17.7202 22.6799 18.362 22.3529C18.9265 22.0653 19.3854 21.6064 19.673 21.0419C20 20.4002 20 19.5601 20 17.8799V13.6799" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <div>
            <h5 className="mb-1 text-gray-900 text-base font-semibold">
              {noteSpecific?.note_heading ? noteSpecific?.note_heading : "Note detail"}
            </h5>
            <p className="text-gray-600">A note for {providerData?.trading_name}</p>
          </div>
        </div>
        <div className="mt-6 px-6">
          <div className="mb-5">
            <label className="text-gray-700 font-medium block mb-1">Note heading</label>
            <div className="input-shadow h-9 text-sm border border-gray-300 rounded-lg py-2 px-3 text-gray-900 leading-tight bg-gray-50 flex items-center">
              {noteSpecific?.note_heading}
            </div>
          </div>
          <div className="mb-5">
            <label className="text-gray-700 font-medium block mb-1">Note</label>
            <div className="min-h-24 text-sm border border-gray-300 rounded-lg py-2 px-3 text-gray-900 leading-tight bg-gray-50 whitespace-pre-wrap">
              {noteSpecific?.note}
            </div>
          </div>
          {attachedFiles && attachedFiles.length > 0 && (
            <div className="mb-5">
              <label className="text-gray-700 font-medium block mb-1">Attachments</label>
              <div className="flex flex-col gap-2">
                {attachedFiles.map((item, index) => (
                  <div key={index} className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-lg p-2">
                    <span className="text-xs text-gray-700 truncate max-w-xs">{item.name}</span>
                    <button
                      type="button"
                      onClick={() => handleFile(item.file)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-semibold px-2"
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex dailog-footer justify-end px-6 pb-6 mt-4">
          <BtnPrimary className="w-full" onClick={onNoteDialogClose}>
            Close
          </BtnPrimary>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        isOpen={dialogEditOpen}
        onClose={onEditDialogClose}
        onRequestClose={onEditDialogClose}
        bodyOpenClassName="overflow-hidden"
        className="addContact-dialog"
      >
        <div className="flex border-b mb-5 pb-5 px-6 pt-6">
          <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <div>
            <h5 className="mb-1 text-gray-900 text-base font-semibold">Edit note</h5>
            <p className="text-gray-600">Update this note for {providerData?.trading_name}</p>
          </div>
        </div>
        {providerFaId && (
          <EditNoteForm
            noteSpecific={editSpecificNote}
            providerFaId={providerFaId}
            providerData={providerData}
            onDialogClose={onEditDialogClose}
            fetchData={GetSpecificOrgNotesCallback}
          />
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={dialogDeleteOpen}
        onClose={onDeleteDialogClose}
        onRequestClose={onDeleteDialogClose}
        bodyOpenClassName="overflow-hidden"
        className="customAlert"
      >
        <div className="px-6 pt-6 text-center">
          <span className="w-12 h-12 rounded-full m-auto bg-error-100 flex items-center justify-center mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <h5 className="text-gray-900 text-lg font-semibold mb-2">Delete note</h5>
          <p className="text-gray-600">Are you sure you want to delete this note? This action cannot be undone.</p>
        </div>
        <div className="mt-8 flex justify-end gap-3 px-6 pb-6">
          <BtnLight disabled={buttonDisabled} onClick={onDeleteDialogClose}>Cancel</BtnLight>
          <BtnPrimary
            disabled={buttonDisabled}
            className="bg-error-600 hover:bg-error-700 border-error-600 hover:border-error-700"
            onClick={handleDeleteNote}
          >
            {buttonDisabled ? <ThreeDotLoader /> : "Delete"}
          </BtnPrimary>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

/* Sub-component: AddNoteForm */
const AddNoteForm = ({ providerFaId, providerData, onDialogClose, fetchData }) => {
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  
  const [noteData, setNotData] = useState({
    note_heading: "",
    note: "",
  });
  const [noteError, setNoteError] = useState({
    note_heading: "",
    note: "",
    note_attach: "",
  });
  const [attachment, setAttachment] = useState({ files: [] });
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [dialogAddOpen, setDialogAddOpen] = useState(false);

  const inputChangeHandler = (e, field) => {
    let value = e.target.value;
    setNotData({ ...noteData, [field]: value });
    setNoteError((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (files) => {
    setAttachment({ files });
    setNoteError((prev) => ({ ...prev, note_attach: "" }));
  };

  const handleFileRemove = (files) => {
    setAttachment({ files });
  };

  const calculateTotalAttachmentSize = () => {
    let totalSize = 0;
    attachment.files.forEach((item) => {
      if (item.size) {
        totalSize += item.size;
      }
    });
    return totalSize;
  };

  const submithandler = (e) => {
    e.preventDefault();

    const headValidation = Validations({
      value: noteData.note_heading,
      Validations: { required: true },
    });
    const contentValidation = Validations({
      value: noteData.note,
      Validations: { required: true },
    });

    if (!headValidation.valid) {
      setNoteError((prev) => ({ ...prev, note_heading: headValidation.error_msg }));
    }
    if (!contentValidation.valid) {
      setNoteError((prev) => ({ ...prev, note: contentValidation.error_msg }));
    }

    const totalSize = calculateTotalAttachmentSize();
    if (totalSize > 25 * 1024 * 1024) {
      setNoteError((prev) => ({ ...prev, note_attach: "Total attachment size cannot exceed 25MB" }));
      return;
    }

    if (headValidation.valid && contentValidation.valid) {
      const payload = {
        ...noteData,
        note_attach: attachment,
        function_assignment_id: providerFaId,
        organisation_id: providerData?.organisation_id,
        trading_name: providerData?.trading_name
      };

      setButtonDisabled(true);
      dispatch(CreateOrgNote(payload))
        .then((response) => {
          if (response.success) {
            setDialogAddOpen(true);
          } else if (response.status === 401) {
            handleUnauthorizedAccess();
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setButtonDisabled(false);
        });
    }
  };

  const onAddDialogClose = () => {
    setDialogAddOpen(false);
    onDialogClose();
    fetchData();
  };

  return (
    <React.Fragment>
      <form onSubmit={submithandler}>
        <div className="mt-6 add-client-form px-6">
          <div className="flex justify-between mb-5">
            <div className="lavel-wd">
              <label className="text-gray-700 font-medium">Note heading*</label>
            </div>
            <div className="columns-input">
              <input
                type="text"
                className="input-shadow h-9 text-sm border-gray-300 rounded-lg appearance-none border w-full py-2 px-3 text-gray-900 leading-tight focus:outline-none focus:shadow-outline"
                value={noteData.note_heading}
                onChange={(e) => inputChangeHandler(e, "note_heading")}
              />
              {noteError.note_heading && (
                <div className="text-red-500 text-sm mt-1">{noteError.note_heading}</div>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-5">
            <div className="lavel-wd">
              <label className="text-gray-700 font-medium">Note*</label>
            </div>
            <div className="columns-input">
              <textarea
                placeholder="Enter a note..."
                className="min-h06 resize-none input-shadow text-sm border-gray-300 rounded-lg appearance-none border w-full py-2 px-3 text-gray-900 leading-tight focus:outline-none focus:shadow-outline"
                value={noteData.note}
                onChange={(e) => inputChangeHandler(e, "note")}
              ></textarea>
              {noteError.note && (
                <div className="text-red-500 text-sm mt-1">{noteError.note}</div>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-5">
            <div className="lavel-wd">
              <label className="text-gray-700 font-medium">Attachment (optional)</label>
            </div>
            <div className="columns-input">
              <Upload
                draggable
                className="border border-gray-200 rounded-xl py-4 px-6 w-full text-center"
                onChange={handleFileChange}
                onFileRemove={handleFileRemove}
                multiple={true}
                maxFileSize={25 * 1024 * 1024}
              >
                <span className="text-blue-700 font-semibold">Click to upload</span>{" "}
                <p className="text-gray-600">(max.25mb)</p>
              </Upload>
              {noteError.note_attach && (
                <div className="text-red-500 text-sm mt-1">{noteError.note_attach}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex dailog-footer px-6 pb-6 gap-3 mt-4">
          <BtnLight disabled={buttonDisabled} onClick={onDialogClose}>Cancel</BtnLight>
          <BtnPrimary disabled={buttonDisabled} type="submit">
            {buttonDisabled ? <ThreeDotLoader /> : "Create note"}
          </BtnPrimary>
        </div>
      </form>

      <Dialog
        isOpen={dialogAddOpen}
        onClose={onAddDialogClose}
        onRequestClose={onAddDialogClose}
        bodyOpenClassName="overflow-hidden"
        className="customAlert"
      >
        <div className="px-6 pt-6 text-center">
          <span className="w-12 h-12 rounded-full m-auto bg-success-100 flex items-center justify-center mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="2" width="20" height="20" rx="10" fill="#D1FADF" stroke="#ECFDF3" strokeWidth="2" />
              <path d="M16 10L11 15L8 12" stroke="#039855" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <p className="text-base text-gray-900 font-semibold text-center mt-4">Note added successfully!</p>
        </div>
        <div className="mt-8 flex justify-center pb-6 px-6">
          <BtnLight onClick={onAddDialogClose}>Close</BtnLight>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

/* Sub-component: EditNoteForm */
const EditNoteForm = ({ noteSpecific, providerFaId, providerData, onDialogClose, fetchData }) => {
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();

  const [noteData, setNotData] = useState({
    note_heading: "",
    note: "",
  });
  const [noteError, setNoteError] = useState({
    note_heading: "",
    note: "",
    note_attach: "",
  });
  
  const [existingFiles, setExistingFiles] = useState([]);
  const [removedAttachments, setRemovedAttachments] = useState([]);
  const [attachment, setAttachment] = useState({ files: [] });
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [dialogEditOpen, setDialogEditOpen] = useState(false);

  useEffect(() => {
    if (noteSpecific) {
      setNotData({
        note_heading: noteSpecific.note_heading || "",
        note: noteSpecific.note || "",
      });

      let files = [];
      if (noteSpecific.attachment && noteSpecific.attachment.length > 0) {
        noteSpecific.attachment.forEach((data) => {
          const file = data.note_attachment;
          const file_id = data.note_attachment_id;
          const parts = data.note_attachment.split("-");
          let FileName = file;
          if (parts.length >= 3) {
            FileName = parts[parts.length - 1];
            FileName = FileName.replace(/%/g, ' ');
          }
          files.push({
            name: FileName,
            file: file,
            id: file_id
          });
        });
      }
      setExistingFiles(files);
      setRemovedAttachments([]);
    }
  }, [noteSpecific]);

  const inputChangeHandler = (e, field) => {
    let value = e.target.value;
    setNotData({ ...noteData, [field]: value });
    setNoteError((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (files) => {
    setAttachment({ files });
    setNoteError((prev) => ({ ...prev, note_attach: "" }));
  };

  const handleFileRemove = (files) => {
    setAttachment({ files });
  };

  const handleRemoveExistingFile = (item) => {
    setRemovedAttachments([...removedAttachments, item.id]);
    setExistingFiles(existingFiles.filter(f => f.id !== item.id));
  };

  const calculateTotalAttachmentSize = () => {
    let totalSize = 0;
    attachment.files.forEach((item) => {
      if (item.size) {
        totalSize += item.size;
      }
    });
    return totalSize;
  };

  const submithandler = (e) => {
    e.preventDefault();

    const headValidation = Validations({
      value: noteData.note_heading,
      Validations: { required: true },
    });
    const contentValidation = Validations({
      value: noteData.note,
      Validations: { required: true },
    });

    if (!headValidation.valid) {
      setNoteError((prev) => ({ ...prev, note_heading: headValidation.error_msg }));
    }
    if (!contentValidation.valid) {
      setNoteError((prev) => ({ ...prev, note: contentValidation.error_msg }));
    }

    const totalSize = calculateTotalAttachmentSize();
    if (totalSize > 25 * 1024 * 1024) {
      setNoteError((prev) => ({ ...prev, note_attach: "Total attachment size cannot exceed 25MB" }));
      return;
    }

    if (headValidation.valid && contentValidation.valid) {
      const payload = {
        note_id: noteSpecific.note_id,
        ...noteData,
        note_attach: attachment,
        removed_attachments: JSON.stringify(removedAttachments),
        function_assignment_id: providerFaId,
        organisation_id: providerData?.organisation_id,
        trading_name: providerData?.trading_name
      };

      setButtonDisabled(true);
      dispatch(UpdateOrgNote(payload))
        .then((response) => {
          if (response.success) {
            setDialogEditOpen(true);
          } else if (response.status === 401) {
            handleUnauthorizedAccess();
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setButtonDisabled(false);
        });
    }
  };

  const onEditDialogClose = () => {
    setDialogEditOpen(false);
    onDialogClose();
    fetchData();
  };

  return (
    <React.Fragment>
      <form onSubmit={submithandler}>
        <div className="mt-6 add-client-form px-6">
          <div className="flex justify-between mb-5">
            <div className="lavel-wd">
              <label className="text-gray-700 font-medium">Note heading*</label>
            </div>
            <div className="columns-input">
              <input
                type="text"
                className="input-shadow h-9 text-sm border-gray-300 rounded-lg appearance-none border w-full py-2 px-3 text-gray-900 leading-tight focus:outline-none focus:shadow-outline"
                value={noteData.note_heading}
                onChange={(e) => inputChangeHandler(e, "note_heading")}
              />
              {noteError.note_heading && (
                <div className="text-red-500 text-sm mt-1">{noteError.note_heading}</div>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-5">
            <div className="lavel-wd">
              <label className="text-gray-700 font-medium">Note*</label>
            </div>
            <div className="columns-input">
              <textarea
                placeholder="Enter a note..."
                className="min-h06 resize-none input-shadow text-sm border-gray-300 rounded-lg appearance-none border w-full py-2 px-3 text-gray-900 leading-tight focus:outline-none focus:shadow-outline"
                value={noteData.note}
                onChange={(e) => inputChangeHandler(e, "note")}
              ></textarea>
              {noteError.note && (
                <div className="text-red-500 text-sm mt-1">{noteError.note}</div>
              )}
            </div>
          </div>

          {existingFiles && existingFiles.length > 0 && (
            <div className="flex justify-between mb-5">
              <div className="lavel-wd">
                <label className="text-gray-700 font-medium">Existing Attachments</label>
              </div>
              <div className="columns-input">
                <div className="flex flex-col gap-2">
                  {existingFiles.map((item, index) => (
                    <div key={index} className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-lg p-2">
                      <span className="text-xs text-gray-700 truncate max-w-xs">{item.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingFile(item)}
                        className="text-red-500 hover:text-red-700 text-xs font-semibold px-2"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between mb-5">
            <div className="lavel-wd">
              <label className="text-gray-700 font-medium">Add Attachment (optional)</label>
            </div>
            <div className="columns-input">
              <Upload
                draggable
                className="border border-gray-200 rounded-xl py-4 px-6 w-full text-center"
                onChange={handleFileChange}
                onFileRemove={handleFileRemove}
                multiple={true}
                maxFileSize={25 * 1024 * 1024}
              >
                <span className="text-blue-700 font-semibold">Click to upload</span>{" "}
                <p className="text-gray-600">(max.25mb)</p>
              </Upload>
              {noteError.note_attach && (
                <div className="text-red-500 text-sm mt-1">{noteError.note_attach}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex dailog-footer px-6 pb-6 gap-3 mt-4">
          <BtnLight disabled={buttonDisabled} onClick={onDialogClose}>Cancel</BtnLight>
          <BtnPrimary disabled={buttonDisabled} type="submit">
            {buttonDisabled ? <ThreeDotLoader /> : "Update note"}
          </BtnPrimary>
        </div>
      </form>

      <Dialog
        isOpen={dialogEditOpen}
        onClose={onEditDialogClose}
        onRequestClose={onEditDialogClose}
        bodyOpenClassName="overflow-hidden"
        className="customAlert"
      >
        <div className="px-6 pt-6 text-center">
          <span className="w-12 h-12 rounded-full m-auto bg-success-100 flex items-center justify-center mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="2" width="20" height="20" rx="10" fill="#D1FADF" stroke="#ECFDF3" strokeWidth="2" />
              <path d="M16 10L11 15L8 12" stroke="#039855" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <p className="text-base text-gray-900 font-semibold text-center mt-4">Note updated successfully!</p>
        </div>
        <div className="mt-8 flex justify-center pb-6 px-6">
          <BtnLight onClick={onEditDialogClose}>Close</BtnLight>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

export default ProviderNotesPage;
